import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  X,
  Settings2,
  MapPin,
  Tag,
  CheckSquare,
  Square,
  Zap,
  ShieldCheck,
  Globe,
  Save,
  Info,
} from "lucide-react";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";
import { domain } from "../../../security";
import Loader from "../../loader/Loader";

// ──────────────────────────────────────────────────────────
// Price type definitions — single source of truth
// ──────────────────────────────────────────────────────────
const PRICE_TYPES = [
  {
    key: "showVatInc",
    label: "VAT Inclusive",
    abbr: "VAT Inc",
    description: "Customer-facing SRP with VAT already included",
    color: "emerald",
    icon: ShieldCheck,
  },
  {
    key: "showVatEx",
    label: "VAT Exclusive",
    abbr: "VAT Ex",
    description: "Price before VAT — typically used for B2B",
    color: "blue",
    icon: Tag,
  },
  {
    key: "showReseller",
    label: "Reseller Price",
    abbr: "Reseller",
    description: "Special discounted rate for accredited resellers",
    color: "violet",
    icon: Zap,
  },
  {
    key: "showZeroRated",
    label: "Zero Rated",
    abbr: "0%",
    description: "VAT zero-rated transactions (government, exports, etc.)",
    color: "amber",
    icon: Globe,
  },
];

// Tailwind color map — needs static strings so PurgeCSS keeps them
const COLOR = {
  emerald: {
    checked: "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200/60",
    unchecked:
      "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30",
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    icon: "text-emerald-600",
    checkBox: "bg-emerald-500 border-emerald-500",
    label: "text-emerald-800",
  },
  blue: {
    checked: "border-blue-400 bg-blue-50 ring-2 ring-blue-200/60",
    unchecked:
      "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/30",
    badge: "bg-blue-100 text-blue-700 border-blue-200",
    icon: "text-blue-600",
    checkBox: "bg-blue-500 border-blue-500",
    label: "text-blue-800",
  },
  violet: {
    checked: "border-violet-400 bg-violet-50 ring-2 ring-violet-200/60",
    unchecked:
      "border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/30",
    badge: "bg-violet-100 text-violet-700 border-violet-200",
    icon: "text-violet-600",
    checkBox: "bg-violet-500 border-violet-500",
    label: "text-violet-800",
  },
  amber: {
    checked: "border-amber-400 bg-amber-50 ring-2 ring-amber-200/60",
    unchecked:
      "border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/30",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    icon: "text-amber-600",
    checkBox: "bg-amber-500 border-amber-500",
    label: "text-amber-800",
  },
};

// ──────────────────────────────────────────────────────────
// CheckboxCard — one price-type tile
// ──────────────────────────────────────────────────────────
const CheckboxCard = ({ priceType, checked, onChange }) => {
  const c = COLOR[priceType.color];
  const Icon = priceType.icon;

  return (
    <button
      type="button"
      onClick={() => onChange(priceType.key)}
      className={`w-full text-left rounded-xl border-2 p-4 transition-all duration-200 cursor-pointer select-none ${
        checked ? c.checked : c.unchecked
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Custom checkbox */}
        <div
          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
            checked ? c.checkBox : "border-slate-300 bg-white"
          }`}
        >
          {checked && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>

        {/* Icon + labels */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon size={15} className={c.icon} />
            <span
              className={`text-sm font-semibold ${
                checked ? c.label : "text-slate-700"
              }`}
            >
              {priceType.label}
            </span>
            <span
              className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${c.badge}`}
            >
              {priceType.abbr}
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            {priceType.description}
          </p>
        </div>
      </div>
    </button>
  );
};

// ──────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────
const AddPersonalizedItems = ({ onClose, refreshData, itemToEdit = null }) => {
  const currentUserID = useSelector(selectUserID);
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState([]);

  const [formData, setFormData] = useState({
    configName: "",
    locationId: "",
    showVatInc: true,
    showVatEx: false,
    showReseller: false,
    showZeroRated: false,
  });

  // ── Fetch locations for dropdown ──
  useEffect(() => {
    axios
      .get(`${domain}/api/Locations`)
      .then((r) => setLocations(r.data))
      .catch(() => toast.error("Failed to load locations."));
  }, []);

  // ── Populate form when editing ──
  useEffect(() => {
    if (itemToEdit) {
      setFormData({
        configName: itemToEdit.configName || "",
        locationId: itemToEdit.locationId ?? "",
        showVatInc: itemToEdit.showVatInc ?? true,
        showVatEx: itemToEdit.showVatEx ?? false,
        showReseller: itemToEdit.showReseller ?? false,
        showZeroRated: itemToEdit.showZeroRated ?? false,
      });
    }
  }, [itemToEdit]);

  // ── Toggle a single price-type checkbox ──
  const handleToggle = (key) => {
    setFormData((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Guard: at least one must be checked ──
  const activeCount = PRICE_TYPES.filter((pt) => formData[pt.key]).length;

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.configName.trim())
      return toast.warning("Please enter a configuration name.");
    if (activeCount === 0)
      return toast.warning("Select at least one price type to display.");

    setIsLoading(true);
    const payload = {
      configName: formData.configName.trim(),
      locationId:
        formData.locationId !== "" ? parseInt(formData.locationId) : null,
      showVatInc: formData.showVatInc,
      showVatEx: formData.showVatEx,
      showReseller: formData.showReseller,
      showZeroRated: formData.showZeroRated,
      userID: currentUserID,
    };

    try {
      if (itemToEdit) {
        await axios.put(
          `${domain}/api/PersonalizedPricing/${itemToEdit.id}`,
          payload,
        );
        toast.success("Configuration updated successfully!");
      } else {
        await axios.post(`${domain}/api/PersonalizedPricing`, payload);
        toast.success("Configuration saved successfully!");
      }
      refreshData();
      onClose();
    } catch {
      toast.error("Failed to save configuration.");
    } finally {
      setIsLoading(false);
    }
  };

  const isEdit = !!itemToEdit;

  return (
    <div className="flex flex-col w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden relative max-h-[90vh]">
      {isLoading && <Loader />}

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-100/60 text-indigo-600">
            <Settings2 size={19} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800 tracking-tight">
              {isEdit
                ? "Edit Price Display Config"
                : "New Price Display Config"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Choose which price types are visible in this setup
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto p-6">
        <form
          id="personalizedForm"
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Config Name */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
              Configuration Name <span className="text-red-400">•</span>
            </label>
            <input
              type="text"
              required
              placeholder='e.g. "Main Branch — POS Display"'
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
              value={formData.configName}
              onChange={(e) =>
                setFormData((p) => ({ ...p, configName: e.target.value }))
              }
            />
          </div>

          {/* Location (optional) */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
              <MapPin size={13} className="opacity-60" /> Linked Location
              <span className="ml-1 text-[10px] font-normal text-slate-400 normal-case tracking-normal">
                (optional — leave blank for global)
              </span>
            </label>
            <div className="relative">
              <MapPin
                size={14}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <select
                className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none appearance-none transition-all"
                value={formData.locationId}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, locationId: e.target.value }))
                }
              >
                <option value="">🌐 Global (applies to all locations)</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.locationName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100" />

          {/* Price type checkboxes */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Price Types to Display
                <span className="text-red-400 ml-1">•</span>
              </label>
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  activeCount > 0
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {activeCount} selected
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {PRICE_TYPES.map((pt) => (
                <CheckboxCard
                  key={pt.key}
                  priceType={pt}
                  checked={formData[pt.key]}
                  onChange={handleToggle}
                />
              ))}
            </div>

            {/* Info hint */}
            <div className="mt-3 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
              <Info size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
              <span>
                Checked price types will appear as columns in the POS and as
                input fields in the Selling Price form for this configuration.
              </span>
            </div>
          </div>
        </form>
      </div>

      {/* ── Footer ── */}
      <div className="px-6 py-4 border-t border-slate-100 bg-gradient-to-r from-slate-50/50 to-white flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
        >
          Cancel
        </button>
        <button
          form="personalizedForm"
          type="submit"
          disabled={activeCount === 0}
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={15} />
          {isEdit ? "Update Config" : "Save Config"}
        </button>
      </div>
    </div>
  );
};

export default AddPersonalizedItems;
